/**
 * Resolves an OAuth `returnUrl` to a root-relative path on `origin`, or
 * `undefined` when following it could leave that origin.
 *
 * Two input shapes are accepted:
 *
 * - a root-relative path with exactly one leading `/`. `//host` and `/\host`
 *   are refused before parsing because browsers read both as
 *   protocol-relative;
 * - an absolute URL whose origin is exactly `origin`. This is what the Canvas
 *   sign-in producers in `components/auth` write today: `window.location.href`
 *   or `window.location.origin` plus a path.
 *
 * Either shape is then parsed by the WHATWG URL parser against `origin`, and
 * the parsed origin must equal `origin`. That second check is what refuses
 * inputs that only look relative, such as a tab between the slashes (the
 * parser strips it, leaving `//host`), or an absolute URL with userinfo
 * (`https://origin-host@evil.example/`). A different scheme or port on the
 * same host is a different origin and is refused too.
 *
 * The result is always `pathname + search + hash`, never the raw input, so
 * the caller navigates within the origin even when the input was absolute.
 *
 * @param returnUrl - The already URL-decoded `returnUrl` from the OAuth
 *   `state`. A missing key or a non-string value yields `undefined`.
 * @param origin - The origin to stay on, normally `window.location.origin`.
 *   A trailing slash is tolerated.
 * @return The root-relative path to navigate to, or `undefined` when
 *   `returnUrl` is missing, not a string, empty, neither root-relative nor an
 *   absolute URL, protocol-relative, on another origin, or unparseable.
 * @throws Never; parse failures yield `undefined`.
 */
export function resolveSameOriginReturnUrl({
  returnUrl,
  origin,
}: {
  returnUrl: unknown;
  origin: string;
}): string | undefined {
  if (typeof returnUrl !== 'string' || returnUrl === '') {
    return undefined;
  }
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(returnUrl);
  const isRootRelative = returnUrl.startsWith('/');
  if (!hasScheme && !isRootRelative) {
    return undefined;
  }
  if (returnUrl.startsWith('//') || returnUrl.startsWith('/\\')) {
    return undefined;
  }
  try {
    const expectedOrigin = new URL(origin).origin;
    const parsed = new URL(returnUrl, expectedOrigin);
    if (parsed.origin !== expectedOrigin) {
      return undefined;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return undefined;
  }
}

/**
 * The path to land on after sign-in when the OAuth `returnUrl` was missing or
 * refused by {@link resolveSameOriginReturnUrl}.
 *
 * @param courseId - The course id carried in the OAuth `state`. Anything
 *   other than a non-empty string sends the user to the site root.
 * @return `/{courseId}/studenthome` for a usable course id, otherwise `/`.
 * @throws Never.
 */
export function defaultReturnPath({courseId}: {courseId: unknown}): string {
  if (typeof courseId !== 'string' || courseId === '') {
    return '/';
  }
  return `/${courseId}/studenthome`;
}
